import { Alert, Avatar, CircularProgress, Snackbar } from "@mui/material";
import { ConnectionType } from "protico-sdk";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useAlert from "../../../hooks/useAlert";
import useBasicProfile from "../../../hooks/useBasicProfile";
import {
  ConnectionState,
  useConnectionCheck,
} from "../../../hooks/useConnectionCheck";
import useSocialConnection from "../../../hooks/useSocialConnection";
import { TaskStateType } from "../../../types/types";
import { getProticoClient } from "../../../utils/api";
import { truncateUsername } from "../../../utils/format.utils";
// import CancelBlockUserPage from "../../FlashChat/components/ProfileModal/pages/CancelBlockUserPage";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelBlockUserModal from "../../../components/FriendAction/CancelBlockUserModal";
import ProfileModal from "../../../components/ProfileModal";
import { useEventWebsocket } from "../../../hooks/useEventWebsocket";
import { useRouter } from "../../../hooks/useRouter";
import { ROUTES } from "../../../routes";
import ChatListItemLoader from "../../DMPage/ChatListItemLoader";
import "../index.css";
import { useWeb3Auth } from "../../../hooks/useWeb3Auth";
import Blockies from "react-blockies";

type Prop = {
  targetAddress: string;
  isRequest?: boolean;
};
const FriendItem = ({ targetAddress, isRequest = false }: Prop) => {
  const { t } = useTranslation();
  const { goBack, push } = useRouter();
  const { basicProfile } = useBasicProfile(targetAddress);
  const [cancelBlockPopOpen, setCancelBlockPopOpen] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const { address } = useWeb3Auth();
  const [isAddingFriend, setIsAddingFriend] = useState(false);
  const { refetch: renewUserSocialConnection, contacts } =
    useSocialConnection();
  const [isOpen, alertContent, isError, setOptionalAlertContent] = useAlert();
  const { isConnectionStateFetching, connectionCheck } =
    useConnectionCheck(targetAddress);
  const { sendEvent } = useEventWebsocket();
  const handleOpenCancelBlock = () => {
    setCancelBlockPopOpen(true);
  };
  const handleCloseCancelBlock = () => {
    setCancelBlockPopOpen(false);
  };
  const handleAcceptFriend = async () => {
    if (!address || !targetAddress) return;
    setIsAddingFriend(true);
    try {
      // TODO: see if user want a nickname
      // TODO: add alertModal
      const addedContact = await getProticoClient().contact.addOne(address, {
        connectionType: ConnectionType.CONNECT,
        target: targetAddress,
        alias: "",
      });
      sendEvent("connection", "confirmed", targetAddress);
      // addOne upserts the contact, so we need to filter out the old one
      const newConnections = contacts.filter(
        (c) => c.target !== addedContact.target
      );
      renewUserSocialConnection([...newConnections, addedContact]);
      // Update SocialConnections successfully
      setOptionalAlertContent(
        t("Alert_Content_Success"),
        TaskStateType.SUCCESS
      );
    } catch (e) {
      setOptionalAlertContent(
        t("Alert_Content_Profile_Update_Fail"),
        TaskStateType.FAIL
      );
    }
    setIsAddingFriend(false);
  };
  const getAction = () => {
    switch (connectionCheck.connectionState) {
      case ConnectionState.WAITING:
        return (
          <div className="friend-item-actions-waiting">
            {t("SocialConnection_Waiting_Btn")}
          </div>
        );
      case ConnectionState.FRIEND:
        return (
          <button
            className="friend-item-actions"
            onClick={(e) => {
              e.stopPropagation();
              push({
                pathname: ROUTES.DM_PAGE + `/${targetAddress}`,
                search: `?alias=${name}`,
              });
            }}
          >
            {t("SocialConnection_Chat_Btn")}
          </button>
        );
      case ConnectionState.BLOCK:
        return (
          <button
            className="friend-item-actions"
            onClick={handleOpenCancelBlock}
          >
            {t("SocialConnection_Unblock_Btn")}
          </button>
        );
      case ConnectionState.REQUESTED:
        return (
          <button className="friend-item-actions" onClick={handleAcceptFriend}>
            {isAddingFriend ? (
              <CircularProgress
                style={{ color: "#fff", marginTop: "5px", padding: "8px" }}
                size={15}
              />
            ) : (
              t("SocialConnection_Accept_Btn")
            )}
          </button>
        );
    }
  };

  const getAddress = () => {
    if (!basicProfile) return;
    if (basicProfile.isENSVerified) return basicProfile.name;
    return targetAddress;
  };

  const getView = () => {
    if (!basicProfile || isConnectionStateFetching) {
      return <ChatListItemLoader />;
    }
    return (
      // TODO make renew state function
      <React.Fragment>
        {profileModalVisible && (
          <ProfileModal
            address={targetAddress}
            uuid={targetAddress}
            setProfileModalVisible={setProfileModalVisible}
          />
        )}
        <Snackbar
          open={isOpen}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            variant="filled"
            severity={isError ? "error" : "success"}
            color={isError ? "error" : "info"}
          >
            <strong>{alertContent}</strong>
          </Alert>
        </Snackbar>
        <CancelBlockUserModal
          open={cancelBlockPopOpen}
          onClosePage={handleCloseCancelBlock}
          address={targetAddress}
        />
        <div
          className="friend-item-container"
          onClick={() => {
            setProfileModalVisible(true);
          }}
        >
          {basicProfile.avatar_url ? (
            <Avatar
              src={basicProfile.avatar_url}
              style={{
                width: "40px",
                height: "40px",
              }}
            />
          ) : (
            <Blockies
              seed={targetAddress}
              size={10}
              scale={4}
              color="#ddd"
              className="identicon"
            />
          )}

          <div className="friend-item-description-container">
            {truncateUsername(connectionCheck.name)} (
            {truncateUsername(getAddress())})
            {!!basicProfile.isENSVerified && (
              <VerifiedIcon
                color="primary"
                fontSize="small"
                style={{
                  verticalAlign: "middle",
                  margin: "-1px 0 0 4px",
                }}
              />
            )}
          </div>
          {getAction()}
        </div>
      </React.Fragment>
    );
  };

  return <div>{getView()}</div>;
};
export default FriendItem;
